import clsx from 'clsx';

import { Badge } from '../Badge';
import { TooltipInfo } from '../Tooltip';

import styles from './styles/Comparison.module.scss';

export type ComparisonProps = {
  comparisonLabel: string;
  leftValue: number;
  middleText?: string;
  middleValue?: number;
  rightValue: number;
  tooltipText?: string;
};

const validateValues = (values: number[], componentName = 'Comparison'): boolean => {
  const sum = values.reduce((acc, val) => acc + val, 0);
  const expectedSum = 100;

  if (sum !== expectedSum) {
    console.warn(`${componentName} component: The sum of values must equal ${expectedSum}. Current sum: ${sum}`);
    return false;
  }

  return true;
};

const Comparison = ({
  comparisonLabel,
  leftValue,
  middleText = 'DRAW',
  middleValue = 0,
  rightValue,
  tooltipText,
  ...props
}: ComparisonProps) => {
  if (!validateValues([leftValue, middleValue, rightValue])) {
    return null;
  }

  const leftWidth = `${leftValue}%`;
  const middleWidth = `${middleValue}%`;
  const rightWidth = `${rightValue}%`;

  const hasMiddleValue = middleValue > 0;
  const badgeRightAlighned = middleValue > 0 && rightValue <= 20;

  return (
    <div
      aria-label={`${comparisonLabel} comparison bar with ${leftValue}% on the left, ${middleValue}% draw in the middle, and ${rightValue}% on the right.`}
      className={styles.comparisonContainer}
      data-cxy-name="comparison"
      role="region"
      {...props}
    >
      <div className={styles.valuesContainer}>
        <span className={styles.value} data-testid="comparison-left">
          <Badge className={styles.leftBadge}>{leftValue}%</Badge>
        </span>
        <div className={styles.label}>
          {comparisonLabel} {tooltipText && <TooltipInfo>{tooltipText}</TooltipInfo>}
        </div>
        <span className={styles.value} data-testid="comparison-right">
          <Badge className={styles.rightBadge}>{rightValue}%</Badge>
        </span>
      </div>
      <div className={clsx(styles.progressBarContainer, { [styles.hasMiddleValue]: hasMiddleValue })}>
        <div
          className={`${styles.progressBar} ${styles.leftBar}`}
          data-testid="comparison-left-bar"
          style={{ width: leftWidth }}
        />
        <div
          className={styles.separatorContainer}
          data-testid="comparison-middle-bar"
          style={{
            width: middleValue ? `${middleValue}%` : undefined,
          }}
        >
          {middleValue > 0 && (
            <Badge className={clsx(styles.middleBadge, { [styles.badgeRightAligned]: badgeRightAlighned })}>
              {middleText} {middleWidth}
            </Badge>
          )}
        </div>
        <div
          className={`${styles.progressBar} ${styles.rightBar}`}
          data-testid="comparison-right-bar"
          style={{ width: rightWidth }}
        />
      </div>
    </div>
  );
};

export { Comparison };
